'use client'
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"

import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams, useRouter } from 'next/navigation'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { currentTenantQueryPrefix } from '@/hooks/queries/current-tenant'
import { EnterpriseContactDialog } from '@/components/auth/enterprise-contact-dialog'
import { EnterprisePlanCard } from '@/components/auth/enterprise-plan-card'
import { RestartOnboardingCard } from '@/components/onboarding/restart-onboarding-card'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Loader2, ExternalLink, TrendingUp, Shield, Zap, Info, ArrowUpDown, ChevronDown } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PlanComparisonCard } from './plan-comparison-card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { BILLING_CHANNELS, SHOPIFY_CREDITS_PENDING_PARAM } from '@/lib/billing/constants'
import { TENANTS_PERMISSIONS } from '@/lib/auth/permissions.constants'
import { shopifySubscribeAction } from '@/server-actions/shopify/billing'
import { ShopifyTopupCard } from './shopify-topup-card'
import { CreditLifetimeNote } from './credit-lifetime-note'
import { BalanceCard } from './balance-card'
import { PartnerBonusBanner } from './partner-bonus-banner'
import { formatExpiryDate } from './balance-details-dialog'
import { planCreditsExpireAt } from '@/lib/billing/wallet/balance-details'
import { formatPeriodDate, renderPlanRecurrenceLine } from './plan-recurrence'
import { StripePlanChangeDialog, type StripePlanChange } from './stripe-plan-change-dialog'
import {
  BILLING_REDIRECT_QUERY_PARAM,
  BILLING_REDIRECT_POLL_INTERVAL_MS,
  BILLING_REDIRECT_POLL_WINDOW_MS,
} from '@/lib/billing/redirect-marker'
import type {
  BillingPlan,
  BillingOverviewResponse,
  BillingTopupPackage,
} from '@/lib/billing/types'
import {
  billingOverviewQueryKey,
  fetchBillingOverview,
  useBillingOverviewQuery,
} from '@/hooks/queries/billing-overview'
import { useShopifyPlanCreditsStatus } from '@/hooks/queries/shopify-plan-credits'

import { planLimitIncludes } from '@/lib/billing/utils/plan-limits'
import { ShopifyPlanCreditsNotice, ShopifyPlanCreditsProgress } from './shopify-plan-credits-notice'
import { QualitySpendLine } from './quality-spend-line'
import { SpendingAlertsCard } from './spending-alerts-card'
import { UsageChart } from './usage-chart'
import {
  getUsageRows,
} from './tenant-billing-usage-row'
import { useLocale, useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





const OPERATION_TYPE_LABELS: Record<string, string> = {
  topup: 'Top Up',
  initial_grant: 'Initial Grant',
  payment_credit: 'Payment Credit',
  plan_change_credit: 'Plan Change Credit',
  manual_grant: 'Platform Grant',
  manual_deduct: 'Platform Deduct',
  refund: 'Refund',
  credit_expiry: 'Expired',
  referral_bonus: 'Referral Bonus',
  partner_transfer: 'Partner Earnings Transfer',
}

function formatOperationType(type: string): string {
  return OPERATION_TYPE_LABELS[type] ?? type
}

function formatDuration(totalSecs: number): string {
  const h = Math.floor(totalSecs / 3600)
  const m = Math.floor((totalSecs % 3600) / 60)
  const s = totalSecs % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

/**
 * Build the post-redirect URL that Stripe / Customer Portal sends the user
 * back to after a checkout / subscription / topup flow. Adds the
 * `from_billing_redirect=1` marker so the page can poll briefly to absorb
 * the webhook race (see `lib/billing/redirect-marker.ts` for the full
 * coordination story).
 *
 * `cancel_url` deliberately omits the marker — a cancelled flow does not
 * change subscription state and needs no polling.
 */
function buildBillingRedirectUrl(): string {
  if (typeof window === 'undefined') return ''
  const url = new URL(window.location.href)
  url.searchParams.set(BILLING_REDIRECT_QUERY_PARAM, '1')
  return url.toString()
}

// =============================================================================
// TenantBillingPage
// =============================================================================

export function TenantBillingPage({ forceUpgradePrompt = false }: { forceUpgradePrompt?: boolean }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const { formatCredits, formatCreditsShort, formatCreditsSigned } = useCreditFormatters()



  const tUsage = useTranslations("UsageRows")
  const locale = useLocale()
  const { renderUsage, renderUnavailableUsage } = getUsageRows(tUsage, locale)
  const tUi = useTranslations("Billing.BillingTenantBillingPage")
  const tRecurrence = useTranslations("PlanRecurrence")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth, user } = useAuth()
  const { can, activeTenantId } = usePermissions()
  const canManage = can('billing.manage')
  const canRestartOnboarding = can(TENANTS_PERMISSIONS.EDIT_OWN)
  // Operational dates follow the viewer. Usage Summary receives its reporting
  // timezone from the server so its month labels follow the tenant calendar.
  const userTimezone = user?.timezone || 'UTC'
  const searchParams = useSearchParams()
  const router = useRouter()

  const { data, isLoading, isError, refresh: refreshOverview } = useBillingOverviewQuery()
  const queryClient = useQueryClient()
  const [topupLoading, setTopupLoading] = useState<string | null>(null)
  const [customAmount, setCustomAmount] = useState('')
  const [customTopupLoading, setCustomTopupLoading] = useState(false)
  const [upgradeNotice, setUpgradeNotice] = useState<string | null>(null)
  // One-shot notice from the Shopify welcome return (subscribed / pending / kept / error).
  const [shopifyNotice, setShopifyNotice] = useState<{ variant: 'default' | 'destructive'; text: string } | null>(null)
  const [shopifyCreditsPending, setShopifyCreditsPending] = useState(false)
  const shopifyCreditsStatus = useShopifyPlanCreditsStatus(shopifyCreditsPending)
  const [availablePlans, setAvailablePlans] = useState<BillingPlan[]>([])
  const [changePlanLoading, setChangePlanLoading] = useState<string | null>(null)
  const [enterpriseInquiryOpen, setEnterpriseInquiryOpen] = useState(false)
  /**
   * Set when the change-plan POST returned 200 but `refetchUntilPlanChanged`
   * exhausted its budget without seeing `plan.id` flip. We keep the dialog
   * open in a "Stripe accepted, plan name still syncing" state so the page
   * never lands in a half-updated view (new balance, old plan name). Reset
   * on dialog close or on the next change-plan attempt.
   */
  const [planSyncTimedOutFor, setPlanSyncTimedOutFor] = useState<string | null>(null)
  /** The in-place Stripe plan change awaiting confirmation; cleared on cancel or once it settles. */
  const [pendingChange, setPendingChange] = useState<StripePlanChange | null>(null)
  /**
   * When set, a dedicated dialog lists the per-resource overflows from a
   * failed downgrade (handler returns `limits_exceeded` + details.overflows).
   * Separate from `pendingChange` so the user can inspect the list without
   * auto-retrying.
   */
  const [limitsBlock, setLimitsBlock] = useState<{
    planName: string
    overflows: Array<{ resource: string; current: number; new_limit: number }>
  } | null>(null)
  const [postRedirectSyncing, setPostRedirectSyncing] = useState(false)

  // One-shot URL notices (consumed + stripped): upgrade_notice from the register
  // flow, shopify_* from the App Pricing welcome return.
  useEffect(() => {
    const notice = searchParams.get('upgrade_notice')
    const shopifyParams = {
      subscribed: searchParams.get('shopify_subscribed'),
      pending: searchParams.get('shopify_billing_pending'),
      kept: searchParams.get('shopify_billing_kept'),
      error: searchParams.get('shopify_billing_error'),
    }
    if (!notice && !Object.values(shopifyParams).some(Boolean)) return

    if (notice) setUpgradeNotice(notice)
    if (shopifyParams.error) {
      setShopifyNotice({ variant: 'destructive', text: shopifyParams.error })
    } else if (shopifyParams.kept) {
      setShopifyNotice({
        variant: 'default',
        text: 'Billing stays on your current store — its plan is larger. The newly approved subscription was cancelled to prevent double billing.',
      })
    } else if (shopifyParams.pending) {
      setShopifyNotice({
        variant: 'default',
        text: 'Your Shopify subscription is still being confirmed. It will activate automatically — refresh this page in a minute.',
      })
    } else if (shopifyParams.subscribed && searchParams.has(SHOPIFY_CREDITS_PENDING_PARAM)) {
      setShopifyCreditsPending(true)
    } else if (shopifyParams.subscribed) {
      setShopifyNotice({ variant: 'default', text: 'Your Shopify subscription is active.' })
    }

    const params = new URLSearchParams(searchParams.toString())
    for (const key of ['upgrade_notice', 'shopify_subscribed', SHOPIFY_CREDITS_PENDING_PARAM, 'shopify_billing_pending', 'shopify_billing_kept', 'shopify_billing_error', 'plan']) {
      params.delete(key)
    }
    const remaining = params.toString()
    router.replace(remaining ? `/?${remaining}` : '/', { scroll: false })
  }, [searchParams, router])

  useEffect(() => {
    if (shopifyCreditsStatus === 'credited') refreshOverview()
  }, [shopifyCreditsStatus, refreshOverview])

  /**
   * Poll `/api/billing/overview` until `plan.id` matches `expectedPlanId` or we
   * exhaust the budget. Returns `true` if the plan was observed to flip,
   * `false` on timeout — the caller decides what to do with a stale read
   * (currently: keep the dialog open in a "still syncing" state instead of
   * closing into a half-updated page).
   *
   * Used after a successful plan change because `tenant_subscriptions.plan_id`
   * is webhook-owned (`docs/BILLING.md` → "Admin Plan Change" Invariants —
   * applies to self-serve too):
   *   - downgrade: route handler writes `last_plan_change_at` synchronously,
   *     but `customer.subscription.updated` is what flips `plan_id`.
   *   - upgrade (inline charge, card 4242 path): `applyUpgradeFromStripe`
   *     runs synchronously in the route handler and writes `plan_id` itself,
   *     so the first refetch usually wins.
   *   - upgrade (pending-update / 3DS path): `plan_id` only flips on the
   *     `pending_update_applied` webhook AFTER the user finishes 3DS — that
   *     can take longer than the budget. Timeout in that case is expected;
   *     the page surfaces an "Awaiting payment" badge from
   *     `pending_plan_change_initiated_at` instead.
   *
   * Each attempt lands in the shared cache entry, so balance / credit propagate
   * to every subscriber even if `plan_id` itself is still stale.
   */
  const refetchUntilPlanChanged = useCallback(async (
    expectedPlanId: string,
    { maxAttempts = 8, intervalMs = 750 }: { maxAttempts?: number; intervalMs?: number } = {},
  ): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const fresh = await queryClient.fetchQuery({
          queryKey: billingOverviewQueryKey(activeTenantId),
          queryFn: () => fetchBillingOverview(fetchWithAuth),
          staleTime: 0,
        })
        if (fresh.plan?.id === expectedPlanId) return true
      } catch {
        // fetchQuery rejects — swallow so one bad attempt does not abort the loop
      }
      if (attempt < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, intervalMs))
      }
    }
    return false
  }, [queryClient, fetchWithAuth, activeTenantId])

  // Post-redirect sync: when the user lands back from a Stripe checkout /
  // Customer Portal flow, the webhook (`customer.subscription.created` /
  // `invoke.paid`) may still be in flight. The single mount-time fetch above
  // can race ahead and render stale state. Detect the redirect via the
  // `from_billing_redirect=1` query param (set on `return_url` by every
  // checkout call site) and poll briefly to absorb the gap. Strip the param
  // immediately so a manual refresh does not re-trigger the loop.
  useEffect(() => {
    if (searchParams.get(BILLING_REDIRECT_QUERY_PARAM) !== '1') return

    setPostRedirectSyncing(true)

    const params = new URLSearchParams(searchParams.toString())
    params.delete(BILLING_REDIRECT_QUERY_PARAM)
    const remaining = params.toString()
    router.replace(remaining ? `/?${remaining}` : '/', { scroll: false })

    // 1Hz × 10s — webhook delivery is typically < 1s, so usually one extra
    // fetch suffices; the loop is the fallback for slow delivery / Stripe
    // outage. After timeout we just stop the spinner — the UI shows the
    // latest data we managed to load and the user can refresh manually if
    // it still looks stale.
    let attempts = 0
    const maxAttempts = Math.max(1, Math.floor(BILLING_REDIRECT_POLL_WINDOW_MS / BILLING_REDIRECT_POLL_INTERVAL_MS))
    const interval = setInterval(async () => {
      attempts++
      await refreshOverview()
      if (attempts >= maxAttempts) {
        clearInterval(interval)
        setPostRedirectSyncing(false)
      }
    }, BILLING_REDIRECT_POLL_INTERVAL_MS)

    return () => {
      clearInterval(interval)
      setPostRedirectSyncing(false)
    }
    // searchParams is stable per render in app-router; intentionally do not
    // depend on `data` — we want exactly one polling window per redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [plansError, setPlansError] = useState(false)
  const isFreePlan = data?.plan?.price_monthly_cents === 0
  // Paid-plan tenants retain the compact default. Free-plan tenants always
  // see their upgrade choices so the page immediately presents a clear next
  // step without requiring a separate Change Plan click.
  const [changePlanOpen, setChangePlanOpen] = useState(forceUpgradePrompt)
  const changePlanExpanded = isFreePlan || changePlanOpen
  // Ref + handler used by the "Change Plan" CTA in the top Plan card so the
  // user can open the section and jump to it without manually scrolling
  // past usage / balance.
  const changePlanCardRef = useRef<HTMLDivElement>(null)
  const openChangePlanSection = useCallback(() => {
    setChangePlanOpen(true)
    // Defer to next frame so the collapsible has expanded before we measure
    // the target's position — otherwise scrollIntoView lands on the still-
    // collapsed offset and the user ends up mid-section once it opens.
    requestAnimationFrame(() => {
      changePlanCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [])

  // Usage history — last N calendar months loaded from
  // /api/billing/usage-history. Used by the period selector in Usage
  // Summary so the tenant can review prior months, not just the live one.
  // periodStart / periodEnd mark the start of the current and next calendar
  // month respectively (UTC); the widget formats them as "May 2026" etc.
  interface UsagePeriodEntry {
    periodStart: string
    periodEnd: string | null
    isCurrent: boolean
    usage: BillingOverviewResponse['periodUsage']
  }
  const [usageHistory, setUsageHistory] = useState<UsagePeriodEntry[]>([])
  const [usageTimezone, setUsageTimezone] = useState<string | null>(null)
  const [selectedPeriodStart, setSelectedPeriodStart] = useState<string | null>(null)

  // Fetch available plans for upgrade section
  const fetchPlans = useCallback(async () => {
    try {
      setPlansError(false)
      const res = await fetchWithAuth('/api/billing/plans')
      if (!res.ok) { setPlansError(true); return }
      const d = await res.json()
      setAvailablePlans((d.plans ?? []) as BillingPlan[])
    } catch {
      setPlansError(true)
    }
  }, [fetchWithAuth])

  useEffect(() => { if (canManage) fetchPlans() }, [canManage, fetchPlans])

  // Load monthly usage history for the Usage Summary period selector.
  // Lazy + best-effort: a fetch failure doesn't disable the page — Usage
  // Summary falls back to `periodUsage` from the overview payload (live
  // billing-cycle numbers, slightly different framing but better than a
  // blank card on cold start).
  // Initial selection uses functional setState so we don't need
  // `selectedPeriodStart` in the callback deps — otherwise picking a
  // month from the dropdown would re-trigger the effect and refetch.
  const fetchUsageHistory = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/billing/usage-history?months=6')
      if (!res.ok) return
      const d = await res.json()
      const periods = (d.periods ?? []) as UsagePeriodEntry[]
      setUsageHistory(periods)
      setUsageTimezone(d.timezone ?? null)
      if (periods.length > 0) {
        // Only set when nothing is selected yet — avoids snapping the user
        // back to the current month after they pick a historical month and
        // the hook re-runs for any reason.
        setSelectedPeriodStart((cur) => cur ?? periods[0].periodStart)
      }
    } catch { /* swallow — selector hides when history is empty */ }
  }, [fetchWithAuth])

  useEffect(() => { fetchUsageHistory() }, [fetchUsageHistory])

  /**
   * Does this subscription qualify for the in-place change-plan route?
   * Free-checkout tenants have payment_provider='stripe' but stripe_subscription_id=null
   * and must still go through Checkout. See handler gate `no_stripe_subscription_id`.
   */
  const canUseChangePlanRoute = (
    sub: BillingOverviewResponse['subscription'],
  ): boolean => {
    if (!sub) return false
    if (sub.payment_provider !== 'stripe') return false
    if (!sub.stripe_subscription_id) return false
    return ['active', 'past_due', 'trialing'].includes(sub.status)
  }

  /**
   * Request a plan change. For tenants with a live Stripe subscription we
   * open the confirmation AlertDialog — the actual POST happens when the
   * user confirms in `executeChangePlan`. Other tenants (free checkout,
   * Shopify, manual re-subscribe) fall through to the existing Checkout
   * flow for initial subscription creation.
   */
  const handleChangePlan = async (targetPlan: BillingPlan) => {
    const sub = data?.subscription ?? null
    const currentPlan = data?.plan ?? null

    if (canUseChangePlanRoute(sub) && currentPlan) {
      const direction: 'upgrade' | 'downgrade' =
        targetPlan.price_monthly_cents > currentPlan.price_monthly_cents ? 'upgrade' : 'downgrade'
      setPendingChange({ plan: targetPlan, direction })
      return
    }

    // Shopify — subscribe/upgrade is a Shopify-hosted plan-selection redirect
    // (App Pricing); initial subscribe and plan change both go through the hosted page.
    if (targetPlan.billing_channel === BILLING_CHANNELS.SHOPIFY) {
      try {
        setChangePlanLoading(targetPlan.id)
        const result = await shopifySubscribeAction({ planId: targetPlan.id })
        if ('status' in result) {
          toast.error(result.errorMessage || 'Failed to start Shopify subscribe')
          return
        }
        window.location.href = result.url // top-level redirect to Shopify's hosted page
      } catch {
        toast.error(tUi("failedToStartShopifySubscribe"))
      } finally {
        setChangePlanLoading(null)
      }
      return
    }

    // Fallback — Stripe initial-subscription checkout flow.
    try {
      setChangePlanLoading(targetPlan.id)
      const res = await fetchWithAuth('/api/billing/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: targetPlan.id,
          return_url: buildBillingRedirectUrl(),
          cancel_url: window.location.href,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        toast.error(err.error || 'Failed to start plan change')
        return
      }
      const result = await res.json()
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl
      }
    } catch {
      toast.error(tUi("failedToInitiatePlanChange"))
    } finally {
      setChangePlanLoading(null)
    }
  }

  // Visibility gate for the top-of-page "Change Plan" CTA — mirrors every
  // condition the Change Plan section below uses to render an actionable
  // card. Enterprise remains an option even when no standard plan change is
  // available, so eligible tenants always have a useful destination.
  //
  // Reads through `data?.subscription` / `data?.plan` instead of the
  // destructured `subscription` / `plan` because those locals live below
  // the loading/error early-returns (further down in the component) —
  // referencing them here would hit TDZ on every render.
  const hasChangePlanOptions = useMemo(() => {
    if (!canManage) return false
    const sub = data?.subscription
    const currentPlan = data?.plan
    if (!sub || sub.payment_provider === 'manual') return false
    if (!currentPlan) return false
    if (!data?.allowSelfServePlanChange) return false
    if (sub.pending_plan_change_initiated_at) return false
    if (sub.cancel_at_period_end && canUseChangePlanRoute(sub)) return false
    if (plansError) return false
    return true
  }, [
    canManage,
    data?.subscription,
    data?.plan,
    data?.allowSelfServePlanChange,
    plansError,
  ])

  /**
   * POST to /api/billing/subscription/change-plan. The handler returns
   * machine-readable `code`s (see lib/billing/change-plan/handle.ts) so UI
   * can surface specific messages instead of raw errors.
   */
  const executeChangePlan = async (targetPlan: BillingPlan) => {
    setPlanSyncTimedOutFor(null) // reset stale timeout from any prior attempt
    try {
      setChangePlanLoading(targetPlan.id)
      const res = await fetchWithAuth('/api/billing/subscription/change-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: targetPlan.id }),
      })
      const payload = await res.json().catch(() => ({} as Record<string, unknown>))

      if (!res.ok) {
        const code = (payload as { code?: string }).code
        const details = (payload as { details?: Record<string, unknown> }).details
        switch (code) {
          case 'plan_change_disabled':
            toast.error(tUi("planChangesAreCurrentlyDisabledContactYourAdmin"))
            break
          case 'plan_change_cooldown': {
            const retryAfter = (details as { retry_after_seconds?: number } | undefined)?.retry_after_seconds
            const minutes = retryAfter ? Math.max(1, Math.ceil(retryAfter / 60)) : null
            toast.error(
              minutes
                ? `Plan change is on cooldown. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
                : 'Plan change is on cooldown; try again shortly.',
            )
            break
          }
          case 'plan_change_pending':
            toast.error(tUi("aPreviousPlanChangeIsStillAwaitingPayment"))
            break
          case 'cancellation_scheduled':
            toast.error(tUi("yourPlanIsSetToSwitchToFree"))
            break
          case 'limits_exceeded': {
            const overflows = (details as { overflows?: Array<{ resource: string; current: number; new_limit: number }> } | undefined)?.overflows ?? []
            setLimitsBlock({ planName: targetPlan.display_name, overflows })
            break
          }
          case 'config_missing':
            toast.error(tUi("thisPlanIsNotYetConfiguredForSelf"))
            break
          case 'same_plan':
            toast.error(tUi("youAreAlreadyOnThisPlan"))
            break
          case 'same_price_switch':
            toast.error(tUi("lateralPlanSwitchesAtTheSamePriceAre"))
            break
          case 'no_stripe_subscription_id':
          case 'not_stripe_tenant':
          case 'subscription_not_active':
            toast.error(tUi("yourSubscriptionIsNotEligibleForInPlace"))
            break
          default:
            toast.error((payload as { error?: string }).error ?? 'Failed to change plan.')
        }
        setPendingChange(null) // close dialog on rejection
        return
      }

      // A switch to Free is only scheduled: the plan stays until the period end.
      if ('effective_at' in payload) {
        const effectiveAt = formatPeriodDate((payload as { effective_at: string | null }).effective_at, userTimezone, locale)
        toast.success(effectiveAt
          ? `Your plan switches to ${targetPlan.display_name} on ${effectiveAt}.`
          : `Your plan switches to ${targetPlan.display_name} at the end of the billing period.`)
        setPendingChange(null)
        await refreshOverview()
        return
      }
      toast.success(tUi("planChangedSuccessfully"))
      // Wait for the Stripe webhook to flip plan_id (see refetchUntilPlanChanged).
      const settled = await refetchUntilPlanChanged(targetPlan.id)
      if (settled) {
        void queryClient.invalidateQueries({ queryKey: currentTenantQueryPrefix })
        setPendingChange(null)
      } else {
        // Budget exhausted (~6s) without seeing plan.id flip. Don't close
        // into a half-updated page — keep the dialog open in a "still
        // syncing" state and continue polling in the background so the UI
        // self-heals when the webhook finally lands.
        setPlanSyncTimedOutFor(targetPlan.id)
        void refetchUntilPlanChanged(targetPlan.id, { maxAttempts: 30, intervalMs: 2000 })
      }
    } catch {
      toast.error(tUi("failedToChangePlan"))
      setPendingChange(null)
    } finally {
      setChangePlanLoading(null)
    }
  }

  const handleTopup = async (pkg: BillingTopupPackage) => {
    try {
      setTopupLoading(pkg.id)
      const res = await fetchWithAuth('/api/billing/topup/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: pkg.id,
          return_url: buildBillingRedirectUrl(),
          cancel_url: window.location.href,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        alert(err.error || 'Failed to start checkout')
        return
      }
      const { checkoutUrl } = await res.json()
      window.location.href = checkoutUrl
    } catch {
      alert('Failed to start checkout')
    } finally {
      setTopupLoading(null)
    }
  }

  const handleCustomTopup = async () => {
    const dollars = parseFloat(customAmount)
    if (isNaN(dollars) || dollars <= 0) return
    const cents = Math.round(dollars * 100)
    try {
      setCustomTopupLoading(true)
      const res = await fetchWithAuth('/api/billing/topup/custom', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: cents,
          return_url: buildBillingRedirectUrl(),
          cancel_url: window.location.href,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        alert(err.error || 'Failed to start topup')
        return
      }
      const result = await res.json()
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl
      }
    } catch {
      alert('Failed to start topup')
    } finally {
      setCustomTopupLoading(false)
    }
  }

  const handlePortal = async () => {
    try {
      const returnUrl = encodeURIComponent(window.location.href)
      const res = await fetchWithAuth(`/api/billing/subscription/portal?return_url=${returnUrl}`)
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        alert(err.error || 'Failed to open billing portal')
        return
      }
      const { portalUrl } = await res.json()
      window.open(portalUrl, '_blank')
    } catch {
      alert('Failed to open billing portal')
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Guard on data, not on `isError`: the shared entry can be in `error` from someone
  // else's failed poll while this page still has a perfectly good snapshot.
  if (!data) {
    return (
      <div key={isError ? tUi("failedToLoadBillingData") : tUi("noData")} className="text-center text-destructive py-12">
        {isError ? tUi("failedToLoadBillingData") : tUi("noData")}
      </div>
    )
  }

  const { plan, balance, subscription, trial, counters, periodUsage, pricing, transactions, topupPackages } = data
  const currentPlanCreditsExpireAt = plan ? planCreditsExpireAt(balance.grants, plan.id) : null
  const currentPlanCreditsUntil = currentPlanCreditsExpireAt ? formatExpiryDate(currentPlanCreditsExpireAt, userTimezone) : null

  return (
    <div className="min-w-0 space-y-6 p-4 sm:p-6">
      {postRedirectSyncing && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertDescription>
            <span key={tUi("finalizingYourBillingChangeIfThisStaysAfter")}>{tUi("finalizingYourBillingChangeIfThisStaysAfter")}</span></AlertDescription>
        </Alert>
      )}
      {upgradeNotice && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <span><span key={tUi("yourAccountWasCreatedOnTheFreePlan")}>{tUi("yourAccountWasCreatedOnTheFreePlan")}</span></span>{' '}
            <strong>{upgradeNotice}</strong> <span><span key={tUi("fromThisPage")}>{tUi("fromThisPage")}</span></span>
          </AlertDescription>
        </Alert>
      )}

      {shopifyNotice && (
        <Alert variant={shopifyNotice.variant}>
          <Info className="h-4 w-4" />
          <AlertDescription>{shopifyNotice.text}</AlertDescription>
        </Alert>
      )}

      {shopifyCreditsPending && <ShopifyPlanCreditsNotice status={shopifyCreditsStatus} />}

      {subscription?.payment_provider === 'manual' && (
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            <span key={tUi("yourBillingIsManagedByThePlatformTeam")}>{tUi("yourBillingIsManagedByThePlatformTeam")}</span></AlertDescription>
        </Alert>
      )}
      {/* Shopify: billing runs through a connected store. Nudge to connect one before subscribing. */}
      {canManage
        && plan?.billing_channel === BILLING_CHANNELS.SHOPIFY
        && (counters?.shopify_stores_count ?? 0) === 0 && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-600 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          <Info className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <span key={tUi("shopifyBillingRunsThroughYourConnectedStoreConnect")}>{tUi("shopifyBillingRunsThroughYourConnectedStoreConnect")}</span></span>
            <Button size="sm" onClick={() => router.push('/?page=integrations')}>
              <span key={tUi("connectStore")}>{tUi("connectStore")}</span></Button>
          </AlertDescription>
        </Alert>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold break-words"><span key={tUi("billing")}>{tUi("billing")}</span></h1>
          <p className="text-muted-foreground text-sm"><span key={tUi("manageYourPlanAndBalance")}>{tUi("manageYourPlanAndBalance")}</span></p>
        </div>
        {canManage && subscription?.managementAvailable && (
          <Button variant="outline" onClick={handlePortal} size="sm">
            <ExternalLink className="h-4 w-4 mr-1" /> <span><span key={tUi("manageSubscription")}>{tUi("manageSubscription")}</span></span>
          </Button>
        )}
      </div>

      <PartnerBonusBanner />

      {/* Balance + Plan cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <BalanceCard balance={balance} timezone={userTimezone}>
          {shopifyCreditsPending && <ShopifyPlanCreditsProgress status={shopifyCreditsStatus} />}
        </BalanceCard>

        {/* Plan */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardDescription className="flex items-center gap-1">
                <Shield className="h-4 w-4" /> <span><span key={tUi("plan")}>{tUi("plan")}</span></span>
              </CardDescription>
              {/* Inline CTA — opens the Change Plan collapsible below and
                  scrolls it into view. Visible only when there is actually
                  something to switch to (mirrors the section's own gates). */}
              {hasChangePlanOptions && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs gap-1"
                  onClick={openChangePlanSection}
                >
                  <ArrowUpDown className="h-3.5 w-3.5" />
                  <span><span key={tUi("changePlan")}>{tUi("changePlan")}</span></span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold break-words">{plan?.display_name ?? tUi("noPlan")}</div>
            {(() => {
              const line = subscription && plan ? renderPlanRecurrenceLine(subscription, plan, userTimezone, tRecurrence, locale) : null
              return line ? (
                <div className="text-xs text-muted-foreground mt-0.5">{line}</div>
              ) : null
            })()}
            {data?.planExpiry && (
              <div key={data.planExpiry.daysRemaining === 0
                  ? tUi("needToUpgradeNow")
                  : tUi("needToUpgradeInValue0Value1", { value0: String(data.planExpiry.daysRemaining), value1: String(data.planExpiry.daysRemaining === 1 ? 'day' : 'days') })}
                className={`text-xs mt-1 ${
                  data.planExpiry.daysRemaining === 0
                    ? 'text-destructive'
                    : 'text-amber-600'
                }`}
              >
                {data.planExpiry.daysRemaining === 0
                  ? tUi("needToUpgradeNow")
                  : tUi("needToUpgradeInValue0Value1", { value0: String(data.planExpiry.daysRemaining), value1: String(data.planExpiry.daysRemaining === 1 ? 'day' : 'days') })}
              </div>
            )}
            {subscription && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant={subscription.status === 'active' ? 'default' : 'destructive'}>
                  {subscription.status}
                </Badge>
                {subscription.payment_provider === 'manual' && (
                  <Badge variant="outline"><span key={tUi("platformManaged")}>{tUi("platformManaged")}</span></Badge>
                )}
                {/* `Cancels at period end` text removed — superseded by the
                    secondary line above which already surfaces the date. */}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Voice Trial */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Zap className="h-4 w-4" /> <span><span key={tUi("voiceAccess")}>{tUi("voiceAccess")}</span></span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plan?.voice_included ? (
              <div className="text-lg font-semibold text-green-600"><span key={tUi("included")}>{tUi("included")}</span></div>
            ) : trial ? (
              <div>
                <div className="text-lg font-semibold"><span key={tUi("trialActive")}>{tUi("trialActive")}</span></div>
                <div className="text-xs text-muted-foreground mt-1">
                  <span><span key={tUi("budget")}>{tUi("budget")}</span></span> {formatCredits(trial.spent_microusd)} <span>/</span> {trial.budget_microusd != null ? formatCredits(trial.budget_microusd) : '∞'}
                </div>
                <div className="text-xs text-muted-foreground">
                  <span><span key={tUi("expires")}>{tUi("expires")}</span></span> {new Date(trial.expires_at).toLocaleDateString(uiFormattingLocale)}
                </div>
              </div>
            ) : (
              <div className="text-lg text-muted-foreground"><span key={tUi("notAvailable")}>{tUi("notAvailable")}</span></div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resource Usage */}
      {plan && counters && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-1">
              <TrendingUp className="h-4 w-4" /> <span><span key={tUi("resourceUsage")}>{tUi("resourceUsage")}</span></span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {renderUsage(tUsage("voiceAgents"), counters.voice_agents_count, plan.voice_agents_limit)}
              {plan.campaigns_enabled === false
                ? renderUnavailableUsage(tUsage("campaigns"))
                : renderUsage(tUsage("campaigns"), counters.campaigns_count, plan.campaigns_limit)}
              {renderUsage(tUsage("phoneNumbers"), counters.pbx_numbers_active_count, plan.pbx_numbers_limit)}
              {renderUsage(tUsage("inboundFlows"), counters.pbx_flows_count, plan.pbx_flows_limit)}
              {renderUsage(tUsage("knowledgeBases"), counters.knowledge_bases_count, plan.knowledge_bases_limit)}
              {renderUsage(tUsage("knowledgeSize"), Number(counters.knowledge_bases_size_mb.toFixed(2)), plan.knowledge_bases_size_mb_limit)}
              {renderUsage(tUsage("users"), counters.users_count, plan.users_limit)}
              {renderUsage(tUsage("crmIntegrations"), counters.crm_integrations_count, plan.crm_integrations_limit)}
              {planLimitIncludes(plan.shopify_stores_limit) && renderUsage(tUsage("shopifyStores"), counters.shopify_stores_count, plan.shopify_stores_limit)}
              {planLimitIncludes(plan.marketing_channels_limit) && renderUsage(tUsage("marketingChannels"), counters.marketing_channels_count, plan.marketing_channels_limit)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Change Plan — hidden entirely when the self-serve flag is OFF.
          Rendered as a collapsible section (default closed) so the plan grid
          doesn't dominate the page on every visit. The card UI (header + price
          + voice status + resource limits + benefits) is shared with the
          public pricing page and the admin Plans tab via PlanComparisonCard
          → identical look across all three surfaces. */}
      {canManage && subscription && subscription.payment_provider !== 'manual' && plan && data?.allowSelfServePlanChange && (() => {
        // While a pending upgrade is awaiting payment confirmation, suppress
        // the change-plan UI and surface a stand-in card. The route's
        // `plan_change_pending` gate would 409 anyway — better to hide the
        // affordance than to let the tenant click into an error.
        if (subscription.pending_plan_change_initiated_at) {
          return (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-1">
                  <ArrowUpDown className="h-4 w-4" /> <span><span key={tUi("changePlan")}>{tUi("changePlan")}</span></span>
                  <Badge variant="secondary" className="ml-2 font-normal"><span key={tUi("awaitingPayment")}>{tUi("awaitingPayment")}</span></Badge>
                </CardTitle>
                <CardDescription>
                  <span key={tUi("yourPreviousPlanChangeIsAwaitingPaymentConfirmation")}>{tUi("yourPreviousPlanChangeIsAwaitingPaymentConfirmation")}</span></CardDescription>
              </CardHeader>
            </Card>
          )
        }

        const inPlaceEligible = canUseChangePlanRoute(subscription)
        // A scheduled switch to Free blocks in-place changes (the route answers
        // `cancellation_scheduled`); Stripe's Customer Portal can resume it.
        if (inPlaceEligible && subscription.cancel_at_period_end) {
          const switchDate = formatPeriodDate(subscription.current_period_end, userTimezone, locale)
          return (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-1">
                  <ArrowUpDown className="h-4 w-4" /> <span><span key={tUi("changePlan")}>{tUi("changePlan")}</span></span>
                </CardTitle>
                <CardDescription>
                  <span key={switchDate
                    ? tUi("yourPlanSwitchesToFreeOnValue0To", { value0: String(switchDate) })
                    : tUi("yourPlanSwitchesToFreeAtTheEnd")}>{switchDate
                    ? tUi("yourPlanSwitchesToFreeOnValue0To", { value0: String(switchDate) })
                    : tUi("yourPlanSwitchesToFreeAtTheEnd")}</span>
                </CardDescription>
              </CardHeader>
            </Card>
          )
        }

        // Shopify changes plans on its hosted page (redirect) and supports BOTH
        // directions; Shopify owns proration/timing. Other non-in-place tenants
        // (free-checkout / canceled Stripe) stay upgrade-only.
        const allowAnyDirection = inPlaceEligible || plan.billing_channel === BILLING_CHANNELS.SHOPIFY

        // Buckets:
        //   - in-place Stripe: up + down via the change-plan route (proration).
        //   - Shopify: up + down via the hosted plan-selection page (Shopify prorates).
        //   - free-checkout / canceled Stripe: upgrades only (legacy Checkout flow).
        //   - manual tenants: no self-serve UI (outer guard above).
        const candidates = availablePlans.filter(
          (p) => p.billing_channel === plan.billing_channel
            && p.status === 'published'
            && p.id !== plan.id
            && (allowAnyDirection
              // same price is a no-op lateral — exclude it.
              ? p.price_monthly_cents !== plan.price_monthly_cents
              : p.price_monthly_cents > plan.price_monthly_cents),
        )

        if (plansError) return (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-1">
                <ArrowUpDown className="h-4 w-4" /> <span><span key={tUi("changePlan")}>{tUi("changePlan")}</span></span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground"><span><span key={tUi("unableToLoadAvailablePlans")}>{tUi("unableToLoadAvailablePlans")}</span></span> <Button variant="link" className="p-0 h-auto text-sm" onClick={fetchPlans}><span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button></p>
            </CardContent>
          </Card>
        )

        // Single row sorted by price with the current plan anchored in the
        // middle. Reads top-to-bottom as «cheaper ← you are here → pricier»,
        // which matches the way customers think about plan ladders. On Free
        // or top-tier plans the current card lands at one of the edges —
        // that's honest (no fake "no downgrade available" placeholder) and
        // the position itself communicates the asymmetry.
        const sortedRow: Array<{
          plan: BillingPlan
          direction: 'upgrade' | 'downgrade' | 'current'
        }> = [
          ...candidates.map((p) => ({
            plan: p,
            direction:
              (p.price_monthly_cents > plan.price_monthly_cents
                ? 'upgrade'
                : 'downgrade') as 'upgrade' | 'downgrade',
          })),
          { plan, direction: 'current' as const },
        ].sort((a, b) => a.plan.price_monthly_cents - b.plan.price_monthly_cents)
        const optionCount = candidates.length + 1

        return (
          <Card ref={changePlanCardRef}>
            <Collapsible
              open={changePlanExpanded}
              onOpenChange={isFreePlan ? undefined : setChangePlanOpen}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full text-left"
                  aria-expanded={changePlanExpanded}
                  disabled={isFreePlan}
                >
                  <CardHeader className={`rounded-t-lg pb-3 transition-colors ${isFreePlan ? '' : 'cursor-pointer hover:bg-muted/30'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="space-y-1.5">
                        <CardTitle className="text-base flex items-center gap-1">
                          <ArrowUpDown className="h-4 w-4" /> <span><span key={tUi("changePlan")}>{tUi("changePlan")}</span></span>
                          <Badge variant="secondary" className="ml-2 font-normal">
                            {optionCount} <span key={optionCount === 1 ? tUi("option") : tUi("options")}>{optionCount === 1 ? tUi("option") : tUi("options")}</span>
                          </Badge>
                        </CardTitle>
                        <CardDescription>
                          {changePlanDescription(inPlaceEligible, plan.billing_channel, tUi)}
                        </CardDescription>
                      </div>
                      {isFreePlan ? null : (
                        <ChevronDown
                          className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${changePlanExpanded ? 'rotate-180' : ''}`}
                        />
                      )}
                    </div>
                  </CardHeader>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-2">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {sortedRow.map(({ plan: p, direction }) => (
                      <PlanComparisonCard
                        key={p.id}
                        plan={p}
                        variant="tenant-billing"
                        channel={p.billing_channel}
                        direction={direction}
                        onChange={direction === 'current' ? undefined : () => handleChangePlan(p)}
                        loading={changePlanLoading === p.id}
                      />
                    ))}
                    <EnterprisePlanCard
                      onSelect={() => setEnterpriseInquiryOpen(true)}
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )
      })()}

      {plan ? (
        <EnterpriseContactDialog
          open={enterpriseInquiryOpen}
          channel={plan.billing_channel}
          onOpenChange={setEnterpriseInquiryOpen}
          onSubmitted={() => setEnterpriseInquiryOpen(false)}
        />
      ) : null}

      <StripePlanChangeDialog
        change={pendingChange}
        currentPlanName={plan?.display_name ?? null}
        periodEndLabel={formatPeriodDate(subscription?.current_period_end ?? null, userTimezone, locale)}
        currentPlanCreditsUntil={currentPlanCreditsUntil}
        submitting={pendingChange !== null && changePlanLoading === pendingChange.plan.id}
        syncTimedOut={pendingChange !== null && planSyncTimedOutFor === pendingChange.plan.id}
        onConfirm={(target) => { void executeChangePlan(target) }}
        onClose={() => { setPendingChange(null); setPlanSyncTimedOutFor(null) }}
        onManageCard={() => { void handlePortal() }}
      />

      {/* Downgrade blocked by resource overflow — list the specific resources. */}
      <AlertDialog open={limitsBlock !== null} onOpenChange={(open) => { if (!open) setLimitsBlock(null) }}>
        <AlertDialogContent>
          {limitsBlock && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle><span><span key={tUi("downgradeTo")}>{tUi("downgradeTo")}</span></span> {limitsBlock.planName} <span><span key={tUi("blocked")}>{tUi("blocked")}</span></span></AlertDialogTitle>
                <AlertDialogDescription>
                  <span key={tUi("yourCurrentUsageExceedsTheTargetPlanS")}>{tUi("yourCurrentUsageExceedsTheTargetPlanS")}</span></AlertDialogDescription>
              </AlertDialogHeader>
              <ul className="text-sm space-y-1 py-2">
                {limitsBlock.overflows.map((o) => (
                  <li key={o.resource} className="flex justify-between">
                    <span className="font-medium">{o.resource.replace(/_/g, ' ')}</span>
                    <span className="text-muted-foreground"><TranslationText>{tUi.rich("value0InUseTargetLimitValue1", { value0: String(o.current), value1: String(o.new_limit) })}</TranslationText></span>
                  </li>
                ))}
              </ul>
              <AlertDialogFooter>
                <AlertDialogAction onClick={() => setLimitsBlock(null)}><span key={tUi("gotIt")}>{tUi("gotIt")}</span></AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* Top Up Balance — self-serve active/trialing only. Shopify bills through usage
          meters on the merchant's own plan, so it renders its own card. */}
      {canManage && subscription && ['active', 'trialing'].includes(subscription.status) && subscription.payment_provider !== 'manual' && plan?.billing_channel === BILLING_CHANNELS.SHOPIFY && (
        <ShopifyTopupCard onBalanceChanged={refreshOverview} lifetimeMonths={data.topupCreditLifetimeMonths} />
      )}

      {canManage && subscription && ['active', 'trialing'].includes(subscription.status) && subscription.payment_provider !== 'manual' && plan?.billing_channel !== BILLING_CHANNELS.SHOPIFY && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base"><span key={tUi("topUpBalance")}>{tUi("topUpBalance")}</span></CardTitle>
          </CardHeader>
          <CardContent>
            {/* Package cards */}
            {topupPackages.length > 0 && (
              <div className="grid gap-3 md:grid-cols-4">
                {topupPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`border rounded-lg p-4 text-center ${pkg.is_best_value ? 'border-primary ring-1 ring-primary' : ''}`}
                  >
                    <div className="font-semibold">{pkg.name}</div>
                    <div className="text-2xl font-bold mt-1 break-words">{formatCredits(pkg.amount_microusd)}</div>
                    <div className="text-xs text-muted-foreground"><span><span key={tUi("for")}>{tUi("for")}</span></span>{(pkg.price_cents / 100).toFixed(2)}</div>
                    {pkg.is_best_value && <Badge variant="default" className="mt-2"><span key={tUi("bestValue")}>{tUi("bestValue")}</span></Badge>}
                    <Button
                      className="mt-3 w-full"
                      size="sm"
                      onClick={() => handleTopup(pkg)}
                      disabled={topupLoading === pkg.id}
                    >
                      {topupLoading === pkg.id && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                      <span><span key={tUi("buy")}>{tUi("buy")}</span></span>
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {/* Custom amount */}
            <div className={topupPackages.length > 0 ? 'mt-4 pt-4 border-t' : ''}>
              <p className="text-sm text-muted-foreground mb-2">
                <span key={topupPackages.length > 0 ? tUi("orEnter") : tUi("enter")}>{topupPackages.length > 0 ? tUi("orEnter") : tUi("enter")}</span> <span><span key={tUi("aCustomAmount")}>{tUi("aCustomAmount")}</span></span> <span>{plan && plan.min_topup_cents > 0 && `(min $${(plan.min_topup_cents / 100).toFixed(2)})`}</span>
                <span>{plan && plan.topup_bonus_percent > 0 && ` · ${plan.topup_bonus_percent}% bonus applies`}</span>
              </p>
              <div className="flex gap-2 max-w-xs">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    min={(plan?.min_topup_cents ?? 500) / 100}
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder={((plan?.min_topup_cents ?? 500) / 100).toFixed(2)}
                    className="pl-7"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleCustomTopup}
                  disabled={customTopupLoading || !customAmount || parseFloat(customAmount) <= 0}
                >
                  {customTopupLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  <span><span key={tUi("topUp")}>{tUi("topUp")}</span></span>
                </Button>
              </div>
            </div>
            <CreditLifetimeNote subject="Top-up credits" months={data.topupCreditLifetimeMonths} className="mt-4 text-xs text-muted-foreground" />
          </CardContent>
        </Card>
      )}

      {/* Usage Chart (left 3/5) + Usage Summary & Billing Activity stacked (right 2/5),
          placed by row from `md` up rather than nested in a column. Markup order is the
          phone's reading order — chart, summary, then the settings block last, above
          Onboarding — and the two-row placement rebuilds the desktop layout from it. */}
      <div className="grid min-w-0 gap-4 md:grid-cols-5">
        <div className="min-w-0 md:col-span-3 md:row-start-1 md:self-start">
          <UsageChart />
        </div>
        <div className="min-w-0 flex flex-col gap-4 md:col-span-2 md:row-span-2 md:row-start-1">
          {/* Usage Summary — calendar-month selector + per-month breakdown.
              Default selection = current month. History (current + up to 5
              prior months) loaded eagerly via /api/billing/usage-history.
              Months are stable across plan changes (independent of the
              billing cycle); the operational "next charge" date lives on the
              Plan card instead. Cold-start fallback uses `periodUsage` from
              the overview payload (slightly different framing — billing
              cycle, not calendar month — but avoids a blank card while the
              history fetch is in flight). */}
          {(() => {
            const selected = usageHistory.find((p) => p.periodStart === selectedPeriodStart)
            const isViewingCurrent = !selected || selected.isCurrent
            const view = selected?.usage ?? periodUsage
            // Hide the card entirely if there's nothing meaningful to show —
            // current month at zero AND no historical months to switch to.
            if (view.totalCharges === 0 && usageHistory.length <= 1) return null

            const monthLabel = (iso: string) =>
              new Date(iso).toLocaleDateString(uiFormattingLocale, { month: 'long', year: 'numeric', timeZone: usageTimezone ?? userTimezone })
            const formatPeriodLabel = (entry: UsagePeriodEntry) =>
              `${monthLabel(entry.periodStart)}${entry.isCurrent ? ' (current)' : ''}`
            const selectedLabel = selected ? monthLabel(selected.periodStart) : null

            return (
              <Card className="min-w-0">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base flex items-center gap-1">
                      <TrendingUp className="h-4 w-4" /> <span><span key={tUi("usageSummary")}>{tUi("usageSummary")}</span></span>
                    </CardTitle>
                    {usageHistory.length > 1 && (
                      <Select
                        value={selectedPeriodStart ?? ''}
                        onValueChange={(v) => setSelectedPeriodStart(v)}
                      >
                        <SelectTrigger className="h-7 w-[160px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent align="end">
                          {usageHistory.map((entry) => (
                            <SelectItem key={entry.periodStart} value={entry.periodStart} className="text-xs">
                              {formatPeriodLabel(entry)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  {selectedLabel && (
                    <CardDescription>
                      {<span key={tUi("value0Value1", { value0: String(isViewingCurrent ? 'Current month' : 'Past month'), value1: String(selectedLabel) })}>{tUi("value0Value1", { value0: String(isViewingCurrent ? 'Current month' : 'Past month'), value1: String(selectedLabel) })}</span>}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="min-w-0">
                  <div className="grid grid-cols-1 gap-3 [@media(min-width:420px)]:grid-cols-3">
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground"><span key={tUi("text")}>{tUi("text")}</span></div>
                      <div className="text-sm font-bold">{view.textCount} <span className="font-normal text-muted-foreground"><span key={tUi("req")}>{tUi("req")}</span></span></div>
                      <div className="text-xs font-mono text-muted-foreground">{formatCreditsShort(view.textCharges)} <span className="font-sans"><span key={tUi("spent")}>{tUi("spent")}</span></span></div>
                      {view.textCount > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5"><span>~</span>{formatCreditsShort(Math.round(view.textCharges / view.textCount))}<span><span key={tUi("reqAvg")}>{tUi("reqAvg")}</span></span></div>
                      )}
                      <QualitySpendLine usage={view} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground"><span key={tUi("voice")}>{tUi("voice")}</span></div>
                      <div className="text-sm font-bold">{view.voiceCount} <span className="font-normal text-muted-foreground"><span key={tUi("calls")}>{tUi("calls")}</span></span></div>
                      <div className="text-xs font-mono text-muted-foreground">{formatCreditsShort(view.voiceCharges)} <span className="font-sans"><span key={tUi("spent")}>{tUi("spent")}</span></span></div>
                      {view.voiceDurationSecs > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5">{formatDuration(view.voiceDurationSecs)} <span><span key={tUi("total")}>{tUi("total")}</span></span></div>
                      )}
                      {/* Rate row only meaningful for the current period —
                          it's derived from the active plan + voice account
                          pricing, which can shift between periods. */}
                      {isViewingCurrent && pricing.voiceCostPerMinMicrousd != null && pricing.voiceCostMultiplier != null && (
                        <div className="text-xs text-muted-foreground">{formatCreditsShort(Math.round(pricing.voiceCostPerMinMicrousd * pricing.voiceCostMultiplier))}<span><span key={tUi("minRate")}>{tUi("minRate")}</span></span></div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground"><span key={tUi("knowledgeBase")}>{tUi("knowledgeBase")}</span></div>
                      <div className="text-[10px] text-muted-foreground/70"><span key={tUi("indexingAndRAGSearch")}>{tUi("indexingAndRAGSearch")}</span></div>
                      <div className="text-sm font-bold">{view.embeddingCount} <span className="font-normal text-muted-foreground"><span key={tUi("ops")}>{tUi("ops")}</span></span></div>
                      <div className="text-xs font-mono text-muted-foreground">{formatCreditsShort(view.embeddingCharges)} <span className="font-sans"><span key={tUi("spent")}>{tUi("spent")}</span></span></div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t flex items-baseline justify-between">
                    <div className="text-xs text-muted-foreground"><span key={tUi("totalSpend")}>{tUi("totalSpend")}</span></div>
                    <div className="text-sm font-bold font-mono">{formatCredits(view.totalCharges)}</div>
                  </div>
                </CardContent>
              </Card>
            )
          })()}
          {/* Billing Activity */}
          <Card className="min-w-0 flex-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-base"><span key={tUi("billingActivity")}>{tUi("billingActivity")}</span></CardTitle>
            </CardHeader>
            <CardContent className="min-w-0 max-h-[400px] overflow-y-auto p-3 sm:p-6">
              <Table className="min-w-0 table-fixed sm:min-w-max sm:table-auto">
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead className="px-2 text-xs sm:px-4"><span key={tUi("date")}>{tUi("date")}</span></TableHead>
                    <TableHead className="px-2 text-xs sm:px-4"><span key={tUi("type")}>{tUi("type")}</span></TableHead>
                    <TableHead className="px-2 text-right text-xs sm:px-4"><span key={tUi("amount")}>{tUi("amount")}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="whitespace-nowrap px-2 py-1.5 text-xs text-muted-foreground sm:px-4">
                        {new Date(tx.created_at).toLocaleDateString(uiFormattingLocale, { month: 'short', day: 'numeric' })}
                      </TableCell>
                      <TableCell className="px-2 py-1.5 sm:px-4">
                        <Badge variant="outline" className="max-w-full whitespace-normal break-words px-1.5 py-0 text-[10px]">{formatOperationType(tx.operation_type)}</Badge>
                      </TableCell>
                      <TableCell className={`whitespace-nowrap px-2 py-1.5 text-right font-mono text-xs sm:px-4 ${tx.amount_microusd < 0 ? 'text-destructive' : 'text-green-600'}`}>
                        {formatCreditsSigned(tx.amount_microusd)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-4 text-xs">
                        <span key={tUi("noActivityYet")}>{tUi("noActivityYet")}</span></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
        {/* Spending caps + the alerts about them: one `tenant_billing_settings` row,
            so one block with one Save. Shares the usage column's width. */}
        <div className="min-w-0 md:col-span-3 md:row-start-2 md:self-start">
          <SpendingAlertsCard />
        </div>
      </div>

      {canRestartOnboarding ? <RestartOnboardingCard variant="compact" /> : null}
    </div>
  )
}

function changePlanDescription(inPlaceEligible: boolean, channel: string, tUi: ReturnType<typeof useTranslations<"Billing.BillingTenantBillingPage">>): string {
  if (inPlaceEligible) {
    return tUi("switchPlansWithoutANewCheckoutAnUpgrade")
  }
  if (channel === BILLING_CHANNELS.SHOPIFY) {
    return tUi("switchPlansYouLlBeRedirectedToShopify")
  }
  return tUi("upgradeToAHigherPlanYouLlBe")
}
